import * as Dialog from "@radix-ui/react-dialog";
import { NavLink, Outlet, useLocation } from "react-router";
import { AttendeeSyncIcon } from "@/components/brand/attendee-sync-icon";
import { BrandMark } from "@/components/brand-mark";
import { OrgProvider } from "@/lib/org-context";
import { cn } from "@/lib/cn";
import { useExitWizard, useWizardLinkState } from "@/lib/wizard-navigation";

const steps = [
  {
    path: "introduction",
    label: "Introduction",
    description: "Name your stream",
    n: 1,
  },
  {
    path: "select",
    label: "Setup Source",
    description: "Choose your source",
    n: 2,
  },
  {
    path: "destinations",
    label: "Setup Destinations",
    description: "Choose where data goes",
    n: 3,
  },
  {
    path: "configure",
    label: "Configure",
    description: "Set sync preferences",
    n: 4,
  },
  {
    path: "review",
    label: "Review",
    description: "Confirm and activate",
    n: 5,
  },
];

function WizardChrome() {
  const linkState = useWizardLinkState();
  const exitWizard = useExitWizard();
  const { pathname } = useLocation();
  const base = "/conferences/new";
  const showWatermark = !pathname.endsWith("/introduction");

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) exitWizard();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            const streamName = e.currentTarget.querySelector<HTMLInputElement>(
              'input[aria-label="Stream Name"]',
            );
            if (streamName) {
              e.preventDefault();
              streamName.focus();
            }
          }}
          className={cn(
            "rs-wizard rs-wizard-split fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-2rem)] w-[min(68rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col",
            "overflow-hidden rounded-rs border border-rs-border bg-rs-elevated shadow-rs",
          )}
        >
          <Dialog.Title className="sr-only">
            Create a New Sync Stream
          </Dialog.Title>
          <aside className="rs-wizard-rail">
            <BrandMark showIcon={false} size="sm" />
            <nav aria-label="Setup progress" className="rs-wizard-steps">
              {steps.map((step) => (
                <NavLink
                  key={step.path}
                  to={`${base}/${step.path}`}
                  state={linkState}
                  className={({ isActive }) =>
                    cn(
                      "rs-wizard-step",
                      isActive
                        ? "rs-step-active text-rs-accent"
                        : "text-rs-ink-muted",
                    )
                  }
                >
                  <span className="rs-wizard-step-n">{step.n}</span>
                  <span className="rs-wizard-step-text">
                    <span className="rs-wizard-step-label">{step.label}</span>
                    <span className="rs-wizard-step-description">
                      {step.description}
                    </span>
                  </span>
                </NavLink>
              ))}
            </nav>
            <p className="rs-wizard-rail-note">
              Your event. Your systems. Connected.
            </p>
          </aside>
          <main className="rs-wizard-body min-h-0">
            {showWatermark ? (
              <AttendeeSyncIcon
                decorative
                variant="mono"
                className="rs-wizard-watermark"
              />
            ) : null}
            <div className="rs-wizard-body-inner">
              <Outlet />
            </div>
          </main>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function WizardLayout() {
  return (
    <OrgProvider>
      <WizardChrome />
    </OrgProvider>
  );
}
